import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { Dialog } from './accounts/AccountBits';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import type { OrderCancellation, OrderCancelResult } from '../../types/nursery';

/**
 * Cancel an order. The server says what stops it (an invoice: those are
 * credited, not cancelled) and what it lets go: stock back to free, the order
 * off its load, an online payment refunded. Cancelling runs the same check.
 */
export function CancelOrderDialog({ orderId, orderNumber, onClose, onCancelled }: {
  orderId: number;
  orderNumber: string;
  onClose: () => void;
  onCancelled: (result: OrderCancelResult) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CancelOrderDialog' });
  const [check, setCheck] = useState<OrderCancellation | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getOrderCancellation(orderId)
      .then((res) => (res.success ? setCheck(res.data) : setError(errorMessage(res, t('checkFailed')))))
      .catch((e) => setError(errorMessage(e, t('checkFailed'))));
  }, [orderId, t]);

  const handleCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.cancelOrder(orderId, reason.trim() || null);
      if (res.success) onCancelled(res.data);
      else setError(errorMessage(res, t('cancelFailed')));
    } catch (e) {
      setError(errorMessage(e, t('cancelFailed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={t('title', { number: orderNumber })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('keep')}</Button>
          {check?.can_cancel && (
            <Button variant="danger" onClick={handleCancel} disabled={busy}>
              {busy ? t('cancelling') : t('cancel')}
            </Button>
          )}
        </>
      }
    >
      {!check && !error && <p className="text-sm text-neutral-500">{t('checking')}</p>}

      {check && !check.can_cancel && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 space-y-1">
          {check.blockers.map((b) => <p key={b} className="break-words">{b}</p>)}
        </div>
      )}

      {check?.can_cancel && (
        <>
          <ul className="text-sm text-neutral-700 list-disc pl-5 space-y-1">
            {check.trees_released > 0 ? (
              <li className="break-words">
                {t('treesReleased', {
                  count: check.trees_released,
                  lots: check.lots.map((l) => `${l.lot_code || t('lotFallback', { id: l.lot_id })}: ${l.allocated}`).join(', '),
                })}
              </li>
            ) : (
              <li>{t('noStock')}</li>
            )}
            {check.load && <li className="break-words">{t('offLoad', { number: check.load.load_number })}</li>}
            {check.refund_amount != null && (
              <li className="break-words">
                {check.refund_method === 'cash'
                  ? t('refundCash', { amount: money(check.refund_amount) })
                  : t('refundCard', { amount: money(check.refund_amount) })}
              </li>
            )}
            {check.payment_pending && (
              <li className="break-words">{t('paymentPending')}</li>
            )}
            {check.backorders.length > 0 && (
              <li className="break-words">
                {t('backorders', { count: check.backorders.length, numbers: check.backorders.map((b) => b.order_number).join(', ') })}
              </li>
            )}
          </ul>
          {check.trees_picked > 0 && (
            <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 break-words">
              {t('treesPicked', { count: check.trees_picked })}
            </p>
          )}
          <div>
            <label htmlFor="cancel-reason" className="block text-sm font-medium text-neutral-700 mb-1">{t('reason')}</label>
            <textarea id="cancel-reason" rows={2} maxLength={1000} value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>
      )}
    </Dialog>
  );
}
